import { useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import {
  aggregateWeek,
  weekdayShort,
  dayNumber,
  monthShort,
} from "../lib/journalAggregate";
import { FAMOUS_QUAKES, weekAround, shiftWeek, isThisWeek, fmtWeekLabel } from "../lib/famousQuakes";
import { useHistoricalQuakes } from "../hooks/useHistoricalQuakes";

const IDS = {
  journal: "sleep-journal",
  entry: "journal-entry",
  scroll: "journal-scroll",
  prev: "journal-prev",
  next: "journal-next",
  timeTravel: "time-travel-picker",
  weekPrev: "week-prev",
  weekNext: "week-next",
  thisWeekBtn: "this-week-btn",
};

const toneAccent = {
  wake: "hsl(14 66% 32%)",
  restless: "hsl(22 62% 38%)",
  twitching: "hsl(26 46% 32%)",
  sigh: "hsl(30 22% 30%)",
  still: "hsl(30 18% 40%)",
  deep: "hsl(30 14% 48%)",
};

const Sparkline = ({ hourly, tone }) => {
  const max = Math.max(1, ...hourly);
  const stroke = toneAccent[tone] || "hsl(24 22% 25%)";
  const w = 220;
  const h = 44;
  const step = w / 24;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {/* Zero line */}
      <line
        x1="0"
        y1={h - 3}
        x2={w}
        y2={h - 3}
        stroke="hsl(24 22% 10%)"
        strokeWidth="0.5"
        opacity="0.35"
      />
      {hourly.map((v, i) => {
        const bh = (v / max) * (h - 6);
        return (
          <rect
            key={`hr-${i}`}
            x={i * step + 0.5}
            y={h - 3 - bh}
            width={step - 1.4}
            height={bh}
            fill={stroke}
            opacity={v === 0 ? 0.18 : 0.85}
          />
        );
      })}
      {/* Midnight / noon markers */}
      {[0, 6, 12, 18].map((h2) => (
        <line
          key={h2}
          x1={h2 * step}
          y1={h - 3}
          x2={h2 * step}
          y2={h - 1}
          stroke="hsl(24 22% 10%)"
          strokeWidth="0.5"
          opacity="0.5"
        />
      ))}
      <text x="0" y={h} fontSize="7" fill="hsl(24 22% 45%)" className="mono">
        00
      </text>
      <text x={12 * step - 4} y={h} fontSize="7" fill="hsl(24 22% 45%)" className="mono">
        12
      </text>
      <text x={24 * step - 12} y={h} fontSize="7" fill="hsl(24 22% 45%)" className="mono">
        24
      </text>
    </svg>
  );
};

const Entry = ({ day, isToday }) => {
  const ann = day.annotation;
  const accent = toneAccent[ann.tone];

  // Restlessness bar with tick marks
  const pct = Math.max(3, Math.min(100, day.restlessness));

  return (
    <article
      data-testid={IDS.entry}
      className="min-w-[300px] sm:min-w-[340px] snap-start relative border border-[hsl(var(--ink))]/45 bg-[hsl(var(--paper-2))]/70 p-5 flex flex-col gap-4"
    >
      {/* Torn-paper corner */}
      <div
        aria-hidden
        className="absolute top-0 right-0 w-6 h-6"
        style={{
          background:
            "linear-gradient(225deg, hsl(var(--paper)) 45%, transparent 45%)",
          borderLeft: "1px dashed hsl(24 22% 22% / 0.4)",
          borderBottom: "1px dashed hsl(24 22% 22% / 0.4)",
        }}
      />
      {/* Date */}
      <header className="flex items-baseline justify-between">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-5xl leading-none">{dayNumber(day.date)}</span>
          <div className="flex flex-col leading-tight">
            <span className="mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--ink-2))]">
              {weekdayShort(day.date)}
            </span>
            <span className="mono text-[10px] text-[hsl(var(--ink-mid))]">
              {monthShort(day.date)}
            </span>
          </div>
        </div>
        {isToday && (
          <span
            className="mono text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border"
            style={{ borderColor: accent, color: accent }}
          >
            Tonight
          </span>
        )}
      </header>

      {/* Restlessness */}
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-2))]">
            Restlessness
          </span>
          <span className="mono text-[10px]" style={{ color: accent }}>
            {day.restlessness}
          </span>
        </div>
        <div className="h-[6px] w-full bg-[hsl(var(--ink))]/12 relative">
          <div
            className="h-full transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%`, backgroundColor: accent }}
          />
          {/* Tick marks at 25/50/75 */}
          {[25, 50, 75].map((t) => (
            <span
              key={t}
              className="absolute top-0 h-full w-px bg-[hsl(var(--ink))]/25"
              style={{ left: `${t}%` }}
            />
          ))}
        </div>
      </div>

      {/* Sparkline of the day */}
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-2))] mb-1">
          Tremors by hour
        </div>
        <Sparkline hourly={day.hourly} tone={ann.tone} />
      </div>

      {/* Stats row */}
      <dl className="grid grid-cols-3 gap-2 border-t border-[hsl(var(--ink))]/25 pt-3">
        <div>
          <dt className="mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--ink-mid))]">
            Count
          </dt>
          <dd className="font-display text-2xl leading-none mt-1">{day.count}</dd>
        </div>
        <div>
          <dt className="mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--ink-mid))]">
            Peak
          </dt>
          <dd
            className="font-display text-2xl leading-none mt-1"
            style={{ color: (day.maxMag ?? 0) >= 6 ? toneAccent.wake : "inherit" }}
          >
            {day.maxMag != null ? day.maxMag.toFixed(1) : "—"}
          </dd>
        </div>
        <div>
          <dt className="mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--ink-mid))]">
            At
          </dt>
          <dd className="font-display text-2xl leading-none mt-1">
            {day.peakHour != null ? `${String(day.peakHour).padStart(2, "0")}h` : "—"}
          </dd>
        </div>
      </dl>

      {/* Diary annotation */}
      <div className="mt-auto border-l-2 pl-3 py-1" style={{ borderColor: accent }}>
        <div
          className="mono text-[10px] uppercase tracking-[0.22em]"
          style={{ color: accent }}
        >
          {ann.title}
        </div>
        <p className="font-display italic text-[15px] leading-snug text-[hsl(var(--ink))] mt-1">
          &ldquo;{ann.body}&rdquo;
        </p>
      </div>
    </article>
  );
};

export default function SleepJournal({ liveQuakes = [] }) {
  // Current selected week window. `live: true` means use the incoming
  // liveQuakes; otherwise we fetch history for the range.
  const [week, setWeek] = useState(() => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return { start, end, live: true, presetId: "this-week", subtitle: null };
  });

  const historical = useHistoricalQuakes(week.start, week.end, {
    minMag: 3,
    enabled: !week.live,
  });

  const dataQuakes = week.live ? liveQuakes : historical.quakes;
  const loading = week.live ? false : historical.loading;

  const days = useMemo(
    () => aggregateWeek(dataQuakes, { startDate: week.start }),
    // Use timestamps so we don't re-agg on identical dates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataQuakes, week.start.getTime()]
  );
  const totalTremors = days.reduce((s, d) => s + d.count, 0);
  const avgRestless = Math.round(
    days.reduce((s, d) => s + d.restlessness, 0) / Math.max(1, days.length)
  );
  const worstDay = [...days].sort((a, b) => b.restlessness - a.restlessness)[0];
  const peakOfWeek = days.reduce(
    (m, d) => (d.maxMag != null && d.maxMag > (m ?? 0) ? d.maxMag : m),
    null
  );

  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("article");
    const step = card ? card.getBoundingClientRect().width + 16 : 320;
    el.scrollBy({ left: step * dir, behavior: "smooth" });
  };

  const pickPreset = (fq) => {
    const { start, end } = weekAround(fq.date);
    setWeek({ start, end, live: false, presetId: fq.id, subtitle: fq.subtitle });
    // Scroll journal card back to start
    if (scrollerRef.current) scrollerRef.current.scrollTo({ left: 0, behavior: "smooth" });
  };

  const goThisWeek = () => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    setWeek({ start, end, live: true, presetId: "this-week", subtitle: null });
    if (scrollerRef.current) scrollerRef.current.scrollTo({ left: 0, behavior: "smooth" });
  };

  const shift = (dir) => {
    const { start, end } = shiftWeek(week.start, week.end, dir);
    const live = isThisWeek(start, end);
    setWeek({ start, end, live, presetId: live ? "this-week" : "custom", subtitle: null });
  };

  const weekLabel = fmtWeekLabel(week.start, week.end);
  const activePreset = FAMOUS_QUAKES.find((f) => f.id === week.presetId);

  return (
    <section
      data-testid={IDS.journal}
      className="relative border border-[hsl(var(--ink))]/40 bg-[hsl(var(--paper))]/40 overflow-hidden"
    >
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-end justify-between px-5 sm:px-7 py-4 border-b border-[hsl(var(--ink))]/25 gap-4">
        <div>
          <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--ink-2))]">
            Field diary
            {!week.live && (
              <span className="flex items-center gap-1 border border-[hsl(var(--seismic))] text-[hsl(var(--seismic))] px-1.5 py-0.5 tracking-[0.22em]">
                <Clock size={9} /> Archive
              </span>
            )}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl leading-tight mt-1">
            <span className="italic font-light">How he slept,</span>{" "}
            {week.live ? "this week." : activePreset ? `${activePreset.name} week.` : "that week."}
          </h2>
          <div className="mono text-[10px] text-[hsl(var(--ink-mid))] mt-1 tracking-widest">
            {weekLabel}
            {activePreset && (
              <span className="ml-2 italic normal-case tracking-normal font-display text-[13px] text-[hsl(var(--ink-2))]">
                — {activePreset.subtitle}
              </span>
            )}
          </div>
        </div>
        <div className="hidden md:flex items-baseline gap-6 text-right">
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--ink-mid))]">
              Tremors
            </div>
            <div className="font-display text-2xl">{loading ? "…" : totalTremors}</div>
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--ink-mid))]">
              Peak
            </div>
            <div
              className="font-display text-2xl"
              style={{ color: (peakOfWeek ?? 0) >= 6 ? toneAccent.wake : "inherit" }}
            >
              {peakOfWeek != null ? `M ${peakOfWeek.toFixed(1)}` : "—"}
            </div>
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--ink-mid))]">
              Roughest
            </div>
            <div className="font-display text-2xl">
              {worstDay && worstDay.count > 0 ? weekdayShort(worstDay.date) : "—"}
            </div>
          </div>
        </div>
      </header>

      {/* Time-travel picker */}
      <div
        data-testid={IDS.timeTravel}
        className="flex flex-wrap items-center gap-2 px-5 sm:px-7 py-3 border-b border-[hsl(var(--ink))]/20"
      >
        <button
          onClick={() => shift(-1)}
          aria-label="Previous week"
          data-testid={IDS.weekPrev}
          className="h-8 w-8 flex items-center justify-center border border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5 transition-colors"
        >
          <ChevronLeft size={14} />
        </button>
        <button
          onClick={() => shift(1)}
          aria-label="Next week"
          data-testid={IDS.weekNext}
          className="h-8 w-8 flex items-center justify-center border border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          disabled={week.live}
        >
          <ChevronRight size={14} />
        </button>
        <button
          onClick={goThisWeek}
          data-testid={IDS.thisWeekBtn}
          className={`mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border transition-colors ${
            week.live
              ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))] border-[hsl(var(--ink))]"
              : "border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5"
          }`}
        >
          This week
        </button>
        <span className="mx-1 text-[hsl(var(--ink))]/25">·</span>
        {FAMOUS_QUAKES.map((fq) => (
          <button
            key={fq.id}
            onClick={() => pickPreset(fq)}
            data-testid={`preset-${fq.id}`}
            className={`mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border transition-colors ${
              week.presetId === fq.id
                ? "bg-[hsl(var(--seismic))] text-[hsl(var(--paper))] border-[hsl(var(--seismic))]"
                : "border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5 hover:border-[hsl(var(--seismic))]/60"
            }`}
          >
            {fq.name} <span className="opacity-70">M {fq.magnitude.toFixed(1)}</span>
          </button>
        ))}
      </div>

      {/* Scroll controls */}
      <div className="relative">
        <button
          data-testid={IDS.prev}
          onClick={() => scrollBy(-1)}
          aria-label="Previous day"
          className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full border border-[hsl(var(--ink))]/40 bg-[hsl(var(--paper))] hover:bg-[hsl(var(--ink))]/5 transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          data-testid={IDS.next}
          onClick={() => scrollBy(1)}
          aria-label="Next day"
          className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full border border-[hsl(var(--ink))]/40 bg-[hsl(var(--paper))] hover:bg-[hsl(var(--ink))]/5 transition-colors"
        >
          <ChevronRight size={16} />
        </button>

        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-[hsl(var(--paper))]/70 backdrop-blur-[1px]">
            <div className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--ink-2))]">
              Turning to that page…
            </div>
          </div>
        )}

        <div
          ref={scrollerRef}
          data-testid={IDS.scroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-5 sm:px-14 py-5 scrollbar-hide"
          style={{ scrollbarWidth: "thin" }}
        >
          {days.map((d, i) => (
            <Entry key={d.key} day={d} isToday={week.live && i === days.length - 1} />
          ))}
        </div>
      </div>

      <footer className="px-5 sm:px-7 py-3 border-t border-[hsl(var(--ink))]/25 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1">
        <div className="mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--ink-2))]">
          {week.live
            ? "Journal · derived from felt tremors, MMI ≥ 3"
            : "Archive · GeoNet FDSN · magnitude ≥ 3"}
        </div>
        <div className="font-display italic text-sm text-[hsl(var(--ink))]/80">
          &mdash; a dog&apos;s week, told by the earth.
        </div>
      </footer>
    </section>
  );
}
